import { call, put, takeLatest } from 'redux-saga/effects';
import {
  SAVE_CLAIMS_REPORT_PARAMETERS_REQUEST,
  FETCH_SAVED_CLAIMS_REPORTS_REQUEST,
  REMOVE_CLAIMS_REPORT_REQUEST,
  GENERATE_CONVERSATION_REQUEST,
  FETCH_SINGLE_CLAIMS_REPORT_REQUEST,
  EXPORT_REPORT_DATA_REQUEST
} from '@src/middleware/actions/actionTypes';

import {
  saveReportParametersSuccess,
  saveReportParametersFailure,
  saveReportParametersLoading,
  fetchClaimsReportingDataSuccess,
  fetchClaimsReportingDataFailure,
  fetchClaimsReportingDataLoading,
  removeReportSuccess,
  removeReportFailure,
  removeReportLoading,
  generateConversationLoading,
  generateConversationSuccess,
  generateConversationFailure,
  fetchSingleClaimsReportSuccess,
  fetchSingleClaimsReportFailure,
  fetchSingleClaimsReportLoading,
  exportReportDataSuccess,
  exportReportDataFailure,
  exportReportDataLoading
} from '@src/middleware/actions/ClaimsReporting/claimsReportingActions';

import {
  saveClaimsReportParameters,
  generatePreviewClaimsReport,
  fetchSavedClaimsReport,
  removeClaimsReport,
  generateConversation,
  fetchSingleClaimsReport,
  fetchClaimsReportPreviewByJobId,
  fetchClaimsReportingDataByAttachmentID,
} from '@src/middleware/services/ClaimsReporting/claimsReportingService';

import { DownloadService } from '@src/utility/download.service';
import { fetchColumnMapping } from '@src/apps/Reimbursement/ClaimsReporting/ClaimsReporting.config';
import { AMOUNT_COLUMN_NAMES } from '@src/apps/Reimbursement/ClaimsReporting/ClaimsReporting.constants';

function* handleSaveReportParameters(action: any) {
  try {
    yield put(saveReportParametersLoading(true));
    const response = yield call(saveClaimsReportParameters, action.payload);

    if (response.StatusCode !== 201 && response.StatusCode !== 200) {
      throw new Error(
        response.Message ||
          'Failed to save claims report parameters. Please try again.'
      );
    }

    yield put(saveReportParametersSuccess(response.Data));
  } catch (error: any) {
    console.error('Error in handleSaveReportParameters:', error);

    const responseStatus =
      error?.response?.status ||
      error?.StatusCode ||
      error?.statusCode ||
      error?.Status;

    // For 409, prefer the server response body message over the generic axios message
    const errorMessage =
      responseStatus === 409
        ? error?.response?.data?.Message ||
          error?.response?.data?.message ||
          'Report with same name already exist.'
        : error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.Message ||
          error?.message ||
          'Failed to save claims report parameters. Please try again.';

    yield put(saveReportParametersFailure(errorMessage));
  } finally {
    yield put(saveReportParametersLoading(false));
  }
}

function* handleFetchSavedClaimsReports(action: any) {
  try {
    yield put(fetchClaimsReportingDataLoading(true));
    const response = yield call(() => fetchSavedClaimsReport(action.payload));
    if (response.StatusCode !== 200) {
      throw new Error(
        response.Message || 'Failed to fetch saved claims reports'
      );
    }
    yield put(fetchClaimsReportingDataSuccess(response.Data));
    yield put(fetchClaimsReportingDataLoading(false));
  } catch (error) {
    yield put(fetchClaimsReportingDataFailure(error.message));
    yield put(fetchClaimsReportingDataLoading(false));
  }
}

function* handleRemoveClaimsReport(action: any) {
  try {
    yield put(removeReportLoading(true));
    const response = yield call(() => removeClaimsReport(action.payload));
    if (response.StatusCode !== 200) {
      throw new Error(response.Message || 'Failed to remove claims report');
    }
    yield put(removeReportSuccess(response.Data));
    yield put(removeReportLoading(false));
  } catch (error) {
    yield put(removeReportFailure(error.message));
    yield put(removeReportLoading(false));
  }
}

function* handleGenerateConversation(action: any) {
  let finalResponse: any = [];
  try {
    // Setting Loading true to Show Progress Model on UI
    yield put(generateConversationLoading(true));

    // Making API Call to Generate Conversation ID and Message ID
    const response = yield call(() => generateConversation(action.payload));

    if (response.StatusCode !== 200) {
      throw new Error(response.Message || 'Failed to generate conversation');
    }

    // If the first API call is successful, make the second API call to Genereate JOB ID For Polling
    if (response && response.StatusCode === 200) {
      const jobIdResponse = yield call(() =>
        generatePreviewClaimsReport({
          conversationId: response.Data?.conversation_id,
          messageId: response.Data?.message_id,
        })
      );
      if (jobIdResponse.StatusCode !== 200) {
        throw new Error(
          response.Message || 'Failed to genereate JOB ID For Polling'
        );
      }

      // If the second API call is successful, make the third API call to Fetch Claims Reporting Data by JOB ID and Polling until the Status is not IN_PROGRESS
      if (jobIdResponse && jobIdResponse.StatusCode === 200) {
        const MAX_POLL_ATTEMPTS = 200;
        let attachmentReponse = yield call(() =>
          fetchClaimsReportPreviewByJobId(jobIdResponse.Data?.JobId)
        );
        let pollAttempts = 0;
        while (attachmentReponse?.Data?.Status === 'IN_PROGRESS') {
          if (pollAttempts >= MAX_POLL_ATTEMPTS) {
            throw new Error(
              'Polling timed out waiting for job to complete (third call)'
            );
          }
          yield call(() => new Promise((resolve) => setTimeout(resolve, 3000)));
          attachmentReponse = yield call(() =>
            fetchClaimsReportPreviewByJobId(jobIdResponse.Data?.JobId)
          );
          pollAttempts++;
        }
        if (attachmentReponse && attachmentReponse.StatusCode !== 200) {
          throw new Error(
            attachmentReponse.Message ||
              'Failed to fetch claims reporting data by JOB ID with polling'
          );
        }

        // If the third API call is successful, Make Fourth API Call to fetch JSON Response with Attachment ID, Conversation ID and Message ID
        if (attachmentReponse && attachmentReponse.StatusCode === 200) {
          const attachmentReponsePayload = {
            conversationId: attachmentReponse?.Data?.Result?.conversation_id,
            messageId: attachmentReponse?.Data?.Result?.message_id,
            attachmentId:
              attachmentReponse?.Data?.Result?.attachments?.[0]?.attachment_id,
          };
          let finalJSONResponse = yield call(() =>
            fetchClaimsReportingDataByAttachmentID(
              attachmentReponsePayload.conversationId,
              attachmentReponsePayload.messageId,
              attachmentReponsePayload.attachmentId
            )
          );

          if (finalJSONResponse?.Data?.JobId) {
            let polledResponse = yield call(() =>
              fetchClaimsReportPreviewByJobId(finalJSONResponse.Data.JobId)
            );
            let finalPollAttempts = 0;
            while (polledResponse?.Data?.Status === 'IN_PROGRESS') {
              if (finalPollAttempts >= MAX_POLL_ATTEMPTS) {
                throw new Error(
                  'Polling timed out waiting for job to complete (final call)'
                );
              }
              yield call(
                () => new Promise((resolve) => setTimeout(resolve, 3000))
              );
              polledResponse = yield call(() =>
                fetchClaimsReportPreviewByJobId(finalJSONResponse.Data.JobId)
              );
              finalPollAttempts++;
            }
            if (polledResponse && polledResponse.StatusCode !== 200) {
              throw new Error(
                polledResponse.Message ||
                  'Failed to fetch claims report status by JOB ID'
              );
            }
            finalResponse = polledResponse;
          } else {
            finalResponse = finalJSONResponse;
          }

          if (finalJSONResponse && finalJSONResponse.StatusCode !== 200) {
            throw new Error(
              finalJSONResponse.Message ||
                'Failed to fetch claims reporting data by attachment ID'
            );
          }
        }
      }
    }
    yield put(generateConversationSuccess(finalResponse?.Data?.Result));
    yield put(generateConversationLoading(false));
  } catch (error) {
    yield put(generateConversationFailure(error.message));
    yield put(generateConversationLoading(false));
  }
}

function* handleFetchSingleClaimsReport(action: any) {
  try {
    yield put(fetchSingleClaimsReportLoading(true));
    const response = yield call(() => fetchSingleClaimsReport(action.payload));
    if (response.StatusCode !== 200) {
      throw new Error(
        response.Message || 'Failed to fetch single claims report'
      );
    }
    yield put(fetchSingleClaimsReportSuccess(response.Data));
    yield put(fetchSingleClaimsReportLoading(false));
  } catch (error) {
    yield put(fetchSingleClaimsReportFailure(error.message));
    yield put(fetchSingleClaimsReportLoading(false));
  }
}

function* handleExportReportData(action: any) {
  try {
    yield put(exportReportDataLoading(true));
    const sheets: Array<any> = [];
    const exportTitle = 'On Demand Custom Report';

    const { reportData, timePeriod } = action.payload ?? {};

     const rows = reportData?.rows ?? [];
     const columns = reportData?.columns ?? [];
     const hcoNameField = action.payload?.hcoName || '';

    const formattedExportData = rows.map((row: string[]) =>
       columns.reduce((acc: Record<string, string | number>, column: any, index: number) => {
         const mappedName = fetchColumnMapping(column.name);
         const cellValue = row?.[index] ?? '';
         if (AMOUNT_COLUMN_NAMES.has(mappedName)) {
           const num = Number(cellValue);
           acc[mappedName] = cellValue !== '' && isFinite(num) ? num : String(cellValue);
         } else {
           acc[mappedName] = String(cellValue);
         }
         return acc;
       }, {})
     );

    sheets.push({ title: exportTitle, data: formattedExportData });
    const filters = {
      Timeperiod: timePeriod,
    };
    DownloadService.downloadOnDemandReport(
      { sheets },
      {
        fileFormat: '.xlsx',
        fileName: 'On Demand Custom Report',
        journeyName: 'Reimbursement',
        metricName: 'On Demand Custom Report',
        filters,
      },
      hcoNameField
    );
    yield put(exportReportDataSuccess());
  } catch (error) {
    yield put(exportReportDataFailure());
  } finally {
    yield put(exportReportDataLoading(false));
  }
}

export function* claimsReportingSaga() {
  yield takeLatest(
    SAVE_CLAIMS_REPORT_PARAMETERS_REQUEST,
    handleSaveReportParameters
  );
  yield takeLatest(
    FETCH_SAVED_CLAIMS_REPORTS_REQUEST,
    handleFetchSavedClaimsReports
  );
  yield takeLatest(REMOVE_CLAIMS_REPORT_REQUEST, handleRemoveClaimsReport);
  yield takeLatest(GENERATE_CONVERSATION_REQUEST, handleGenerateConversation);
  yield takeLatest(
    FETCH_SINGLE_CLAIMS_REPORT_REQUEST,
    handleFetchSingleClaimsReport
  );
  yield takeLatest(EXPORT_REPORT_DATA_REQUEST, handleExportReportData);
}